import { getAccountUsersWithRetry } from 'api/account/getAccountUsersWithRetry';
import { getUsersUuidsInBatches } from 'api/account/getUsersUuidsInBatches';
import { readAccountUsersBucket } from 'helpers/cacheUtils/accountUsers/readAccountUsersBucket';
import { writeAccountUsersBucket } from 'helpers/cacheUtils/accountUsers/writeAccountUsersBucket';

import { getMissingUserPks } from './getMissingUserPks';
import { mergeUsersUuids } from './mergeUsersUuids';
import { buildAccountUsersList } from './buildAccountUsersList';

/**
 * Gets all account users together with their UUIDs: fetches the users, requests the UUIDs that are
 * not cached yet (reading the rest from cache), persists the result, and returns the combined data.
 *
 * @returns {Promise<{users: Array<Object>, hasMissingUuid: boolean}>} Account users (full user
 *   objects, each with an added `uuid` field) and whether any user is still missing a UUID.
 */
export const loadAccountUsersWithUuids = async () => {
    const usersList = await getAccountUsersWithRetry();
    if (!usersList.length) {
        return { users: [], hasMissingUuid: false };
    }

    const [{ PK_Account }] = usersList;
    const cachedPairs = await readAccountUsersBucket(PK_Account);

    const missingPK = getMissingUserPks(usersList, cachedPairs);
    const freshPairs = await getUsersUuidsInBatches(missingPK);

    const resolvedPairs = mergeUsersUuids(usersList, cachedPairs, freshPairs);
    await writeAccountUsersBucket(PK_Account, resolvedPairs);

    const users = buildAccountUsersList(usersList, resolvedPairs);

    return { users, hasMissingUuid: users.some((user) => !user.uuid) };
};
